import { lazy, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { LoginPage } from '@/features/auth/LoginPage';
import { NotFoundPage } from '@/features/NotFoundPage';

// Telas internas carregadas sob demanda: tira o maplibre (~784KB), recharts e o
// resto das páginas da carga inicial. /login e /devices (primeira tela) ficam
// leves; o mapa só baixa quando o usuário abre /map.
const named = <T extends Record<string, unknown>, K extends keyof T>(
  loader: () => Promise<T>,
  key: K,
) => lazy(() => loader().then((m) => ({ default: m[key] as ComponentType })));

const DashboardPage = named(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage');
const MapPage = named(() => import('@/features/map/MapPage'), 'MapPage');
const DevicesPage = named(() => import('@/features/devices/DevicesPage'), 'DevicesPage');
const SegurancaGrPage = named(() => import('@/features/seguranca-gr/SegurancaGrPage'), 'SegurancaGrPage');
const DeviceDetailPage = named(() => import('@/features/devices/DeviceDetailPage'), 'DeviceDetailPage');
const ReportsPage = named(() => import('@/features/reports/ReportsPage'), 'ReportsPage');
const EventsReportPage = named(() => import('@/features/reports/EventsReportPage'), 'EventsReportPage');
const SummaryReportPage = named(() => import('@/features/reports/SummaryReportPage'), 'SummaryReportPage');
const TripsReportPage = named(() => import('@/features/reports/TripsReportPage'), 'TripsReportPage');
const StopsReportPage = named(() => import('@/features/reports/StopsReportPage'), 'StopsReportPage');
const RouteReportPage = named(() => import('@/features/reports/RouteReportPage'), 'RouteReportPage');
const HoursReportPage = named(() => import('@/features/reports/HoursReportPage'), 'HoursReportPage');
const GeofencesPage = named(() => import('@/features/geofences/GeofencesPage'), 'GeofencesPage');
const MaintenancePage = named(() => import('@/features/maintenance/MaintenancePage'), 'MaintenancePage');
const NotificationsPage = named(() => import('@/features/notifications/NotificationsPage'), 'NotificationsPage');
const NotificationRulesPage = named(() => import('@/features/notifications/NotificationRulesPage'), 'NotificationRulesPage');
const ProfilePage = named(() => import('@/features/profile/ProfilePage'), 'ProfilePage');
const UsersPage = named(() => import('@/features/users/UsersPage'), 'UsersPage');
const SettingsPage = named(() => import('@/features/settings/SettingsPage'), 'SettingsPage');
const AddDevicePage = named(() => import('@/features/settings/AddDevicePage'), 'AddDevicePage');
const ClientsPage = named(() => import('@/features/settings/ClientsPage'), 'ClientsPage');
const LinksPage = named(() => import('@/features/settings/LinksPage'), 'LinksPage');
const InfoPage = named(() => import('@/features/info/InfoPage'), 'InfoPage');

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/devices" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/devices/:id" element={<DeviceDetailPage />} />
          <Route path="/seguranca-gr" element={<SegurancaGrPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/events" element={<EventsReportPage />} />
          <Route path="/reports/summary" element={<SummaryReportPage />} />
          <Route path="/reports/trips" element={<TripsReportPage />} />
          <Route path="/reports/stops" element={<StopsReportPage />} />
          <Route path="/reports/route" element={<RouteReportPage />} />
          <Route path="/reports/hours" element={<HoursReportPage />} />
          <Route path="/geofences" element={<GeofencesPage />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/notifications/rules" element={<NotificationRulesPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/devices/new" element={<AddDevicePage />} />
          <Route path="/settings/devices/edit/:id" element={<AddDevicePage />} />
          <Route path="/settings/clients" element={<ClientsPage />} />
          <Route path="/settings/links" element={<LinksPage />} />
          <Route path="/info" element={<InfoPage />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
